import {
  addFavorite as requestAddFavorite,
  DEFAULT_FAVORITES_TTL_MS,
  KuxFavorite,
  removeFavorite as requestRemoveFavorite,
} from '@kaltura-ott/tvpil';
import { action, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { singleton } from 'tsyringe';

import { getFavoriteDictionary } from '../../rhino/data/favorite/getFavoriteDictionary';
import { addFavoriteChannel, deleteFavoriteChannel } from '../../rhino/data/favorite/mutations';
import { fetchFavoriteChannelsQuery } from '../../rhino/data/favorite/queries';

import { FavoriteQueue } from './favorite-queue';

@singleton()
export class FavoriteRepository {
  @observable.ref favorites: Record<number, KuxFavorite> = {};

  private _ttl = 0;
  private lastUpdate = 0;
  private isUpdating = false;
  // @ts-expect-error TODO Fix
  private updatingPromise: Promise<void>;
  @observable public versionNumber = 0;

  init(ttl: number = DEFAULT_FAVORITES_TTL_MS) {
    this.ttl = ttl;
  }

  public set ttl(value: number) {
    this._ttl = value;
  }

  public get ttl() {
    return this._ttl;
  }

  public async getFavoritesList() {
    if (this.isFavoriteActual) {
      return;
    }
    if (this.isUpdating) {
      await this.updatingPromise;
      return;
    }
    this.isUpdating = true;

    this.updatingPromise = this.fetchList();
    await this.updatingPromise;
    this.lastUpdate = Date.now();
    this.isUpdating = false;
    this.generateVersionNumber();
  }

  get isFavoriteActual() {
    return this.lastUpdate + this.ttl > Date.now();
  }

  private generateVersionNumber(): void {
    this.versionNumber = Math.random();
  }

  public invalidate() {
    this.lastUpdate = 0;
  }

  private async fetchList() {
    const { favoriteDictionary } = await fetchFavoriteChannelsQuery();

    this.favorites = favoriteDictionary;
  }

  getFavorite(linearId: number): KuxFavorite | undefined {
    return this.favorites[linearId];
  }

  public syncLocalState() {
    const favoriteChannels = getFavoriteDictionary();

    if (!favoriteChannels) {
      return;
    }

    this.favorites = favoriteChannels.favoriteDictionary;

    this.generateVersionNumber();
  }

  private async addFavorite(channelId: number): Promise<boolean> {
    addFavoriteChannel(channelId);

    this.syncLocalState();

    try {
      await FavoriteQueue.enqueue(() => lastValueFrom(requestAddFavorite(channelId)));
      return true;
    } catch (e) {
      deleteFavoriteChannel(channelId);

      this.syncLocalState();

      return false;
    }
  }

  private async removeFavorite(channelId: number): Promise<boolean> {
    deleteFavoriteChannel(channelId);

    this.syncLocalState();

    try {
      await FavoriteQueue.enqueue(() => lastValueFrom(requestRemoveFavorite(channelId)));

      return true;
    } catch (e) {
      addFavoriteChannel(channelId);

      this.syncLocalState();

      return false;
    }
  }

  @action
  async setFavorite(state: boolean, channelId: number): Promise<boolean> {
    const completed = state ? await this.addFavorite(channelId) : await this.removeFavorite(channelId);

    return completed;
  }
}
